import { PRODUCT_IMG } from "@/img/productImages";

export default {
  // Navbar
  "nav.brand": "Bitilinks",
  "nav.subtitle": "Navigation RTK",
  "nav.rtkHost": "RTK Host",
  "nav.scenarios": "Scenarios",
  "nav.antennaParts": "Antenna & Parts",
  "nav.selectionQuote": "Selection & Quote",
  "nav.getQuote": "Get Selection Advice",
  "nav.toggleNav": "Toggle Navigation",

  // Hero
  "hero.title":
    "RTK Navigation & Positioning Hardware Matrix for Unmanned Systems",
  "hero.desc":
    "A unified procurement-ready selection system covering RTK hosts, P900/4G/WIFI telemetry, quad-band full-frequency antennas, mounts and power cables — for flight controllers, unmanned vehicles, boats, drones and deformation monitoring.",
  "hero.viewQuote": "View Price List",
  "hero.browseProducts": "Browse Products",

  "hero.stats": [
    { value: "100km", label: "4G host rated telemetry range" },
    { value: "9~28V", label: "Unified DC power for host & radio" },
    { value: "UART+USB", label: "Standard host data interfaces" },
    {
      value: "From ¥10",
      label: "Complete pricing from cables to integrated host",
    },
  ],

  "hero.quickSelectors": [
    {
      tag: "Long-range Data Link",
      name: "BitRtk982-P900, up to 60Km",
      price: "¥4800",
    },
    {
      tag: "Remote Mgmt & Wide Area",
      name: "BitRtk982-EC20, 4G up to 100Km",
      price: "¥4300",
    },
    {
      tag: "Dev & Short/Mid Range",
      name: "BitRtk982-WIFI, range depends on PA",
      price: "¥3900",
    },
  ],

  "hero.productImageTitle": "RTK-982 Series Host",
  "hero.productImageSubtitle": "RTK GNSS High-Precision Navigation",
  "hero.quickSelectLabel": "Quick Selection",

  // Taxonomy
  "taxonomy.subtitle": "Product Taxonomy",
  "taxonomy.title":
    "Organized by Part Number Rules — Find Hardware Categories at a Glance",
  "taxonomy.codeRule": "100X00XXX",
  "taxonomy.codeRuleDesc": "Navigation & Positioning Part Number Rule",

  "taxonomy.categories": [
    { code: "01", title: "GNSS Receiver", desc: "RTK host & integrated host" },
    { code: "02", title: "Telemetry Radio", desc: "WIFI PA, 4G radio" },
    { code: "03", title: "Antenna", desc: "Mushroom-head, magnetic, whip" },
    { code: "04", title: "Accessories", desc: "Fixed-hole & magnetic mounts" },
    { code: "05", title: "Cables", desc: "XT60 to VH3.96 power cables" },
    { code: "06", title: "Software", desc: "Software category" },
  ],

  // Scenarios
  "scenarios.subtitle": "Application Fields",
  "scenarios.title":
    "Engineering Selection Context for Flight Controllers & Unmanned Platforms",

  "scenarios.items": [
    {
      title: "UGV / USV",
      desc: "RTK host series provides UART x1 and USB x1 data interfaces, DC 9~28V power, suitable for flight controllers and mobile platforms.",
      points: ["P900 up to 60Km", "4G up to 100Km", "WIFI with optional PA"],
    },
    {
      title: "UAV / Drone",
      desc: "Quad-band full-frequency magnetic and whip antennas suit lightweight experimental unmanned vehicles and drones.",
      points: [],
    },
    {
      title: "Deformation Monitoring",
      desc: "BitRtkOne001 integrated host and quad-band full-frequency mushroom-head antenna for dam and mountain deformation monitoring.",
      points: [],
    },
    {
      title: "Remote Management",
      desc: "BitRadio-EC20 provides a remote management channel for P900 telemetry RTK hosts.",
      points: [],
    },
  ],

  // Products
  "products.subtitle": "GNSS Receiver",
  "products.title": "RTK Host Series: Select by Telemetry Link",
  "products.desc":
    "Host dimensions, power and interfaces are highly consistent; selection differences mainly come from telemetry channel and range.",
  "products.priceUnit": "/ unit",

  "products.items": [
    {
      id: "100100001",
      tag: "P900",
      name: "BitRtk982-P900",
      desc: "RTK host with P900 telemetry, suitable for flight controllers, unmanned vehicles and boats.",
      specs: [
        { label: "Telemetry Range", value: "Up to 60Km" },
        { label: "Data Interface", value: "UART x1 / USB x1" },
        { label: "Aux Antenna", value: "Yes" },
        { label: "Dimensions", value: "80x50x21mm" },
      ],
      price: "¥4800",
      image: PRODUCT_IMG.product005,
    },
    {
      id: "100100002",
      tag: "4G",
      name: "BitRtk982-EC20",
      desc: "RTK host with 4G telemetry, suitable for wider-range remote deployment.",
      specs: [
        { label: "Telemetry Range", value: "Up to 100Km" },
        { label: "Data Interface", value: "UART x1 / USB x1" },
        { label: "Aux Antenna", value: "Yes" },
        { label: "Dimensions", value: "80x50x21mm" },
      ],
      price: "¥4300",
      image: PRODUCT_IMG.product011,
    },
    {
      id: "100100003",
      tag: "WIFI",
      name: "BitRtk982-WIFI",
      desc: "RTK host with WIFI telemetry, range depends on power amplifier.",
      specs: [
        { label: "Telemetry Range", value: "Depends on PA" },
        { label: "Data Interface", value: "UART x1 / USB x1" },
        { label: "Aux Antenna", value: "Yes" },
        { label: "Dimensions", value: "80x50x21mm" },
      ],
      price: "¥3900",
      image: PRODUCT_IMG.product013,
    },
    {
      id: "100100004",
      tag: "R&D",
      name: "BitRtkOne001",
      desc: "Integrated host for unmanned vehicles, boats and deformation monitoring scenarios.",
      specs: [
        { label: "Telemetry Channel", value: "P900, 4G, lora" },
        { label: "Telemetry Range", value: "1~100Km" },
        { label: "Aux Antenna", value: "No" },
        { label: "Dimensions", value: "φ160x70mm" },
      ],
      price: "¥5300",
      image: PRODUCT_IMG.product015,
    },
  ],

  // Accessories
  "accessories.subtitle": "Radio / Antenna / Parts",
  "accessories.title":
    "Peripherals Centered on Host Link & Antenna Installation",
  "accessories.desc":
    "Radios extend communication range or enable remote management; antennas and mounts are selected by interface, mounting method and platform size; cables cover XT60 to VH3.96 power adapters.",

  "accessories.items": [
    {
      name: "BitWifiPa-8W WIFI Power Amp",
      desc: "Increases host WIFI communication range, SMA female antenna interface, DC 9~28V power.",
      tags: ["65x50x21mm", "¥400"],
      image: PRODUCT_IMG.product016,
    },
    {
      name: "BitRadio-EC20 4G Radio",
      desc: "Provides a remote management channel for P900 telemetry RTK hosts, 1 TTL UART interface.",
      tags: ["80x50x21mm", "¥500"],
      image: PRODUCT_IMG.product011,
    },
    {
      name: "BitAnt-800S Quad-band Mushroom Antenna",
      desc: "BNC interface, suitable for unmanned vehicles, dam and mountain deformation monitoring; mount sold separately.",
      tags: ["φ154x70mm", "¥350"],
      image: PRODUCT_IMG.product008,
    },
  ],

  "quoteBox.title": "From Scenario to BOM, Reduce Back-and-Forth Confirmation",
  "quoteBox.steps": [
    { num: "01", title: "Select Host: P900 / 4G / WIFI / Integrated" },
    {
      num: "02",
      title: "Select Installation: Mushroom, Magnetic, Whip Antenna",
    },
    { num: "03", title: "Add Accessories: Mounts, Power Adapter Cables" },
  ],
  "quoteBox.viewFullQuote": "View Full Price List",

  // Compare Table
  "compare.subtitle": "Selection Table",
  "compare.title": "Core Price & Parameter Comparison",
  "compare.col.id": "Part Number",
  "compare.col.category": "Category",
  "compare.col.model": "Model",
  "compare.col.params": "Key Params",
  "compare.col.range": "Application",
  "compare.col.price": "Price",

  "compare.rows": [
    {
      id: "100100001",
      category: "RTK Host",
      model: "BitRtk982-P900",
      tags: ["P900", "Up to 60Km", "UART x1", "USB x1", "DC 9~28V"],
      range: "Flight Controller, UGV, USV",
      price: "¥4800",
    },
    {
      id: "100100002",
      category: "RTK Host",
      model: "BitRtk982-EC20",
      tags: ["4G", "Up to 100Km", "UART x1", "USB x1", "DC 9~28V"],
      range: "Flight Controller, UGV, USV",
      price: "¥4300",
    },
    {
      id: "100100003",
      category: "RTK Host",
      model: "BitRtk982-WIFI",
      tags: ["WIFI", "Range depends on PA", "UART x1", "USB x1", "DC 9~28V"],
      range: "Flight Controller, UGV, USV",
      price: "¥3900",
    },
    {
      id: "100100004",
      category: "Integrated Host",
      model: "BitRtkOne001",
      tags: ["P900, 4G, lora", "1~100Km", "UART x1", "USB x1", "DC 9~28V", "φ160x70mm"],
      range: "UGV, USV, Deformation Monitoring",
      price: "¥5300",
    },
    {
      id: "100200001",
      category: "Telemetry Radio",
      model: "BitWifiPa-8W",
      tags: ["Extend WIFI range", "SMA female", "DC 9~28V"],
      range: "Host WIFI communication extension",
      price: "¥400",
    },
    {
      id: "100200002",
      category: "Telemetry Radio",
      model: "BitRadio-EC20",
      tags: ["1 TTL", "SMA female", "DC 9~28V"],
      range: "P900 RTK host remote management",
      price: "¥500",
    },
    {
      id: "100300001",
      category: "Antenna",
      model: "BitAnt-800S",
      tags: ["Quad-band mushroom", "BNC", "Mount separate"],
      range: "UGV, dam & mountain deformation monitoring",
      price: "¥350",
    },
    {
      id: "100300002",
      category: "Antenna",
      model: "BitAnt-502",
      tags: ["Quad-band magnetic", "SMA male", "φ48x33mm"],
      range: "Small experimental UGV & UAV",
      price: "¥300",
    },
    {
      id: "100300003",
      category: "Antenna",
      model: "BitAnt-460",
      tags: ["Quad-band whip", "SMA female", "φ28x60mm"],
      range: "UAV / Drone",
      price: "¥200",
    },
    {
      id: "100400001",
      category: "Accessories",
      model: "BitParts-M110SLB",
      tags: ["Fixed-hole mushroom mount", "Screw mount", "φ154x70mm"],
      range: "Mushroom antenna mounting",
      price: "¥150",
    },
    {
      id: "100400002",
      category: "Accessories",
      model: "BitParts-M100SD",
      tags: ["Magnetic mushroom mount", "Magnetic mount", "φ48x33mm"],
      range: "Mushroom antenna mounting",
      price: "¥100",
    },
    {
      id: "100400003",
      category: "Accessories",
      model: "BitParts-B150",
      tags: ["Magnetic fixed mushroom mount", "Magnetic and screw mount", "φ28x60mm"],
      range: "Mushroom antenna mounting",
      price: "¥100",
    },
    {
      id: "100500001",
      category: "Cables",
      model: "VH396-XT60-F-P-100",
      tags: ["XT60 female to VH3.96", "Pin 1 positive", "10cm"],
      range: "Power adapter",
      price: "¥10",
    },
    {
      id: "100500002",
      category: "Cables",
      model: "VH396-XT60-F-N-100",
      tags: ["XT60 female to VH3.96", "Pin 1 positive", "10cm"],
      range: "Power adapter",
      price: "¥10",
    },
    {
      id: "100500003",
      category: "Cables",
      model: "VH396-XT60-F-P-200",
      tags: ["XT60 female to VH3.96", "Pin 1 positive", "20cm"],
      range: "Power adapter",
      price: "¥12",
    },
    {
      id: "100500004",
      category: "Cables",
      model: "VH396-XT60-F-N-200",
      tags: ["XT60 female to VH3.96", "Pin 1 positive", "20cm"],
      range: "Power adapter",
      price: "¥12",
    },
    {
      id: "100500005",
      category: "Cables",
      model: "VH396-XT60-F-P-300",
      tags: ["XT60 female to VH3.96", "Pin 1 positive", "30cm"],
      range: "Power adapter",
      price: "¥15",
    },
    {
      id: "100500006",
      category: "Cables",
      model: "VH396-XT60-F-N-300",
      tags: ["XT60 female to VH3.96", "Pin 1 positive", "30cm"],
      range: "Power adapter",
      price: "¥15",
    },
  ],

  // Footer
  "footer.company":
    "Bitilinks (Suzhou) Software & Information Technology Service Co., Ltd.",
  "footer.backToTop": "Back to Top",

  // Language switch
  "lang.zh": "中文",
  "lang.en": "English",
  "lang.switch": "Switch Language",
};
